import { motion } from "framer-motion";

type OrbState = "idle" | "listening" | "thinking" | "speaking";

interface ConciergeOrbProps {
  state: OrbState;
  statusText: string;
}

export function ConciergeOrb({ state, statusText }: ConciergeOrbProps) {
  const glowIntensity = {
    idle: "0 0 40px rgba(var(--primary-rgb, 59, 130, 246), 0.15)",
    listening: "0 0 60px rgba(var(--primary-rgb, 59, 130, 246), 0.25)",
    thinking: "0 0 80px rgba(var(--primary-rgb, 59, 130, 246), 0.3)",
    speaking: "0 0 70px rgba(var(--primary-rgb, 59, 130, 246), 0.25)",
  };

  return (
    <div className="flex flex-col items-center gap-4" data-testid="concierge-orb">
      <div className="relative">
        {state === "thinking" && (
          <motion.div
            className="absolute inset-[-8px] rounded-full border-2 border-primary/30"
            animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          />
        )}

        <motion.div
          className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 via-primary/10 to-primary/5 border border-primary/20 flex items-center justify-center"
          animate={{
            scale: state === "idle" ? [1, 1.04, 1] : state === "speaking" ? [1, 1.06, 1] : 1,
            boxShadow: glowIntensity[state],
          }}
          transition={{
            scale: { duration: state === "idle" ? 3 : 1.5, repeat: Infinity, ease: "easeInOut" },
            boxShadow: { duration: 0.5 },
          }}
        >
          <div className="w-8 h-8 rounded-full bg-primary/30 flex items-center justify-center">
            {state === "thinking" ? (
              <motion.div className="flex gap-1">
                {[0, 1, 2].map((i) => (
                  <motion.span
                    key={i}
                    className="w-1.5 h-1.5 rounded-full bg-primary"
                    animate={{ y: [0, -4, 0] }}
                    transition={{ duration: 0.6, delay: i * 0.15, repeat: Infinity }}
                  />
                ))}
              </motion.div>
            ) : (
              <div className="w-3 h-3 rounded-full bg-primary/60" />
            )}
          </div>
        </motion.div>
      </div>

      <motion.p
        className="text-xs text-muted-foreground"
        key={statusText}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        data-testid="text-orb-status"
      >
        {statusText}
      </motion.p>
    </div>
  );
}
